import React, { useState, useEffect } from "react";
import { UserSwitchIcon as UserSwitch, XIcon as X, WarningIcon as Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Persistent banner shown across the top of every page while an admin
 * is impersonating another user.
 *
 * Source of truth: `user._impersonator` from `/auth/me`. If present,
 * the banner renders. The "Stop" button calls `/auth/stop-impersonating`
 * (audit-logs the end), restores the admin's original token from
 * localStorage, and hard-reloads.
 *
 * Crimson background, gold border — impossible to miss, so an admin
 * never forgets they're "in" someone else's account.
 */
export default function ImpersonationBanner() {
  const { user, refreshUser } = useAuth() || {};
  const [stopping, setStopping] = useState(false);
  const [target, setTarget] = useState(null);

  useEffect(() => {
    // Cache the target identity from when we started — useful if
    // /auth/me hasn't returned yet or fails momentarily.
    try {
      const raw = localStorage.getItem("impersonation_target");
      if (raw) setTarget(JSON.parse(raw));
    } catch {
      setTarget(null);
    }
  }, [user?.id]);

  const imp = user?._impersonator;
  if (!imp) return null;

  const stop = async () => {
    setStopping(true);
    // Fire-and-forget the audit log — never block the user from
    // returning to admin. Even if the server is down, the local
    // session cleanup + reload still happens.
    api.post("/auth/stop-impersonating").catch(() => {});
    const adminToken = localStorage.getItem("admin_token_before_impersonation");
    if (adminToken) {
      localStorage.setItem("token", adminToken);
      localStorage.removeItem("admin_token_before_impersonation");
    } else {
      // Lost the original token (storage cleared) — force re-login.
      localStorage.removeItem("token");
    }
    localStorage.removeItem("impersonation_target");
    // Hard reload to /admin/users so every cached query is dropped.
    window.location.assign("/admin/users");
  };

  const targetLabel = target?.name || target?.email || user.email;
  const expiresAt = target?.expires_at;
  const minsLeft = expiresAt
    ? Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 60000))
    : null;

  return (
    <div
      className="sticky top-0 z-[100] bg-crimson text-white border-b-2 border-gold shadow-md"
      data-testid="impersonation-banner"
      role="alert"
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center gap-3">
        <Warning size={18} weight="fill" className="text-gold shrink-0" />
        <UserSwitch size={16} weight="duotone" className="shrink-0" />
        <div className="flex-1 min-w-0 text-[12px] sm:text-[13px]">
          <span className="font-bold uppercase tracking-[0.1em] text-gold mr-2">
            Impersonating
          </span>
          <span className="font-semibold truncate inline-block max-w-[40ch] align-middle">
            {targetLabel}
          </span>
          <span className="ml-2 hidden md:inline text-white/70">
            · Signed in by {imp.admin_email}
            {minsLeft !== null && ` · ${minsLeft} min left`}
          </span>
        </div>
        <button
          type="button"
          onClick={stop}
          disabled={stopping}
          className="inline-flex items-center gap-1 rounded-full bg-white text-crimson hover:bg-gold hover:text-ink font-bold uppercase tracking-[0.1em] text-[11px] px-3 py-1.5 transition-colors disabled:opacity-50 shrink-0"
          data-testid="impersonation-stop-btn"
        >
          <X size={11} weight="bold" />
          {stopping ? "Stopping…" : "Stop"}
        </button>
      </div>
    </div>
  );
}
